/**
 * Public type surface for @norkron/web-offline-store.
 *
 * The schema DSL is intentionally structural: a schema is a frozen map of
 * table-name → {@link TableDefinition}. Row types are recovered with
 * {@link InferRow} so consumers get full end-to-end typing without code-gen.
 */

/**
 * Bookkeeping columns the lib stamps onto every stored row. They exist so the
 * sibling `@norkron/web-sync-queue` outbox can read change events cleanly
 * without diffing payloads.
 *
 * - `__version` — monotonically increasing per-row counter. Bumped on every
 *   successful `put`/`update`. Used for optimistic concurrency.
 * - `__updatedAt` — `Date.now()` of the last successful write. Used by the
 *   outbox to order change events and by quota eviction to find stale rows.
 */
export interface RowMeta {
	__version: number
	__updatedAt: number
}

/** A stored row: the consumer payload `T` plus lib bookkeeping columns. */
export type StoredRow<T> = T & RowMeta

/**
 * A key path into row type `T` — every string property of `T`, and plain
 * `string` once the row type has been erased to `unknown`.
 *
 * The erasure clause is load-bearing, not cosmetic. `keyof T` is
 * *contravariant* in `T`, so a bare `keyof T & string` collapses to `never` at
 * `T = unknown` — the widest row type would admit the narrowest key path, and
 * nothing at all would inhabit it. That made `TableDefinition<unknown>` a
 * *bottom* of the family rather than its top: no `TableDefinition<User>` or
 * `TableBuilder<User>` was assignable to it, so `defineSchema` was
 * unsatisfiable for every real row type and each consumer had to widen with
 * `as unknown as TableDefinition` at the schema entry point.
 *
 * Degrading to `string` exactly when `T` is erased makes the erased key path
 * *inhabited* — `TableDefinition<unknown>['keyPath']` is `string`, not `never`.
 * `RowKey<User>` is unchanged (`'id' | 'email' | 'age'`), so `InferRow` still
 * recovers the exact row type and a bogus key path is still rejected.
 *
 * This alone does **not** make `TableDefinition<unknown>` assignable-to from
 * `TableDefinition<User>`, and it cannot: `RowKey<T>` is genuinely neither
 * covariant nor contravariant (an `out T` annotation is rejected by the
 * compiler, correctly — a wider `T` yields plain `string`, which no narrower
 * `T`'s key union admits). TypeScript measures `T` as *invariant* here and
 * compares same-name generic references by that variance with no structural
 * fallback. So the assignable row-erased top of each family is a separate,
 * non-generic interface: {@link AnyTableDefinition} / {@link AnyTableBuilder}.
 *
 * @see `.claude/plans/feature-requests/FR-web-offline-store-tableinput-variance.md`
 */
export type RowKey<T> = unknown extends T ? string : keyof T & string

/** Index definition attached to a table via `table<T>().withIndex(...)`. */
export interface IndexDefinition<T> {
	/** Index name (used by `queryByIndex` / `cursor`). */
	readonly name: string
	/** Keypath into `T` the index is built over (dotted paths allowed by IDB). */
	readonly keyPath: RowKey<T>
	/** When true, the index rejects duplicate keys (IDB `unique` flag). */
	readonly unique: boolean
	/** When true, array-valued keypaths fan out into multiple index entries. */
	readonly multiEntry: boolean
}

/**
 * A single object-store definition. `keyPath` is the in-line primary key
 * property on `T`. Built fluently via {@link table}.
 *
 * To hold a definition for *any* row type, use {@link AnyTableDefinition} —
 * `TableDefinition<unknown>` is not a supertype of `TableDefinition<User>`.
 * {@link RowKey} explains why.
 */
export interface TableDefinition<T = unknown> {
	readonly keyPath: RowKey<T>
	readonly indexes: ReadonlyArray<IndexDefinition<T>>
	/** Marker so `InferRow` can recover the row type. */
	readonly __rowType?: T
}

/** Fluent builder returned by {@link table}. */
export interface TableBuilder<T> {
	/**
	 * Declare a secondary index. Returns a new builder so calls chain.
	 *
	 * @example
	 * table<User>('id').withIndex('byEmail', 'email', { unique: true })
	 */
	withIndex(
		name: string,
		keyPath: RowKey<T>,
		opts?: { unique?: boolean; multiEntry?: boolean },
	): TableBuilder<T>
	/** Materialise the immutable {@link TableDefinition}. */
	build(): TableDefinition<T>
}

/**
 * Row-erased {@link IndexDefinition} — the top of that family. Every
 * `IndexDefinition<T>` is assignable to it.
 */
export interface AnyIndexDefinition {
	readonly name: string
	readonly keyPath: string
	readonly unique: boolean
	readonly multiEntry: boolean
}

/**
 * Row-erased {@link TableDefinition} — the top of that family. Every
 * `TableDefinition<T>` is assignable to it, for any `T`.
 *
 * This exists because `TableDefinition<unknown>` is *not* that supertype and
 * cannot be made into one (see {@link RowKey}). Use this type wherever a table
 * definition is held without caring about its row type — `Schema['tables']`,
 * a heterogeneous collection, a function parameter. Recover the row type with
 * {@link InferRow} at the point of use instead.
 */
export interface AnyTableDefinition {
	readonly keyPath: string
	readonly indexes: ReadonlyArray<AnyIndexDefinition>
	/** Marker so `InferRow` can recover the row type. Erased to `unknown` here. */
	readonly __rowType?: unknown
}

/**
 * Row-erased {@link TableBuilder} — the top of that family. Every
 * `TableBuilder<T>` is assignable to it. Counterpart of
 * {@link AnyTableDefinition}; see {@link RowKey} for why it is needed.
 */
export interface AnyTableBuilder {
	withIndex(
		name: string,
		keyPath: string,
		opts?: { unique?: boolean; multiEntry?: boolean },
	): AnyTableBuilder
	build(): AnyTableDefinition
}

/**
 * A value accepted by {@link defineSchema} for one table: a finished
 * {@link TableDefinition} or a {@link TableBuilder} (auto-`.build()`-ed).
 *
 * Both arms are row-erased, so a builder for *any* row type is accepted;
 * {@link DefinedSchema} recovers each table's concrete row type in the result.
 */
export type TableInput = AnyTableDefinition | AnyTableBuilder

/** A versioned schema: name → table definition, plus the schema version. */
export interface Schema {
	readonly version: number
	readonly tables: Readonly<Record<string, AnyTableDefinition>>
}

/**
 * The precise type {@link defineSchema} returns: a {@link Schema} whose
 * `tables` preserves each input's row type, so {@link InferRow} recovers it.
 *
 * Deliberately **not** `Schema & { tables: … }`. Intersecting merges `Schema`'s
 * `Record<string, AnyTableDefinition>` index signature into `tables`, so every
 * unknown property name resolves instead of erroring: `schema.tables.userz`
 * silently yields `AnyTableDefinition` rather than "Property 'userz' does not
 * exist. Did you mean 'users'?". Declaring the shape standalone keeps table
 * names checked. It stays assignable to {@link Schema} because a mapped type
 * gets an implicit index signature.
 *
 * (`InferRow` works under either form — that was measured, not assumed.)
 */
export type DefinedSchema<M extends Record<string, TableInput>> = {
	readonly version: number
	readonly tables: {
		readonly [K in keyof M]: M[K] extends TableBuilder<infer T>
			? TableDefinition<T>
			: M[K] extends TableDefinition<infer U>
				? TableDefinition<U>
				: never
	}
}

/** Recover the consumer payload type for a given table name. */
export type InferRow<S extends Schema, K extends keyof S['tables']> =
	S['tables'][K] extends TableDefinition<infer T> ? T : never

/**
 * A migration step run inside the IndexedDB `upgradeneeded` transaction.
 * Steps MUST be idempotent — they may run after a partially-applied prior
 * attempt. `fromVersion`/`toVersion` are inclusive-exclusive bounds; a step
 * with `{ to: 3 }` runs when upgrading across the 2→3 boundary.
 */
export interface Migration {
	/** Target version this step migrates the DB *to*. */
	readonly to: number
	/**
	 * Mutate data using the provided upgrade transaction. Called only when the
	 * opening DB's old version is `< to`. Throwing aborts the entire upgrade
	 * (browser-atomic) and the DB stays at its prior version.
	 */
	readonly migrate: (ctx: MigrationContext) => void | Promise<void>
}

/** Handle passed to a {@link Migration} step. */
export interface MigrationContext {
	readonly db: IDBDatabase
	readonly tx: IDBTransaction
	readonly oldVersion: number
	readonly newVersion: number
}

/** Optional AES-GCM encryption config for at-rest payloads. */
export interface EncryptionConfig {
	/** Passphrase a key is derived from via PBKDF2-SHA-256. */
	readonly passphrase: string
	/** PBKDF2 iteration count. Default 210_000 (OWASP 2023 floor). */
	readonly iterations?: number
	/** Stable per-store salt. Default derived from the DB name. */
	readonly salt?: Uint8Array
}

/** Options for {@link createStore}. */
export interface CreateStoreOptions {
	/** IndexedDB database name. */
	readonly dbName: string
	/** Schema (defines tables, indexes, version). */
	readonly schema: Schema
	/** Ordered migration steps. Auto-sorted by `to` ascending. */
	readonly migrations?: ReadonlyArray<Migration>
	/** When set, payloads are AES-GCM encrypted before write. */
	readonly encryption?: EncryptionConfig
	/** Usage fraction (0..1) at which `storage-low` fires. Default 0.8. */
	readonly quotaLowWaterMark?: number
}

/** Result of {@link Store.estimate}. */
export interface QuotaEstimate {
	/** Bytes used by this origin (best-effort; 0 when unsupported). */
	readonly usage: number
	/** Bytes granted to this origin (0 when unsupported). */
	readonly quota: number
	/** `usage / quota`, clamped to [0, 1]. 0 when quota is 0/unknown. */
	readonly ratio: number
	/** True once the value crossed `quotaLowWaterMark`. */
	readonly low: boolean
}

/** Full-DB export envelope produced by {@link Store.exportAll}. */
export interface DatabaseExport {
	readonly lib: '@norkron/web-offline-store'
	readonly libVersion: string
	readonly dbName: string
	readonly schemaVersion: number
	readonly exportedAt: number
	/** table name → array of stored rows (bookkeeping columns included). */
	readonly tables: Record<string, ReadonlyArray<unknown>>
}

/** Discriminated error codes surfaced by store operations. */
export type OfflineStoreErrorCode =
	| 'idb-unavailable'
	| 'migration-failed'
	| 'version-conflict'
	| 'tx-aborted'
	| 'decrypt-failed'
	| 'not-found'
	| 'invalid-input'

/** Single typed error thrown/rejected by every public store operation. */
export class OfflineStoreError extends Error {
	readonly code: OfflineStoreErrorCode
	constructor(code: OfflineStoreErrorCode, message: string, options?: { cause?: unknown }) {
		super(message, options)
		this.name = 'OfflineStoreError'
		this.code = code
	}
}

/** Subset of `IDBKeyRange`-compatible query bounds for `queryByIndex`. */
export interface IndexQuery {
	/** Exact key match (mutually exclusive with range bounds). */
	readonly equals?: IDBValidKey
	readonly lower?: IDBValidKey
	readonly upper?: IDBValidKey
	readonly lowerOpen?: boolean
	readonly upperOpen?: boolean
	/** Max rows returned. Unbounded when omitted. */
	readonly limit?: number
	/** 'next' (asc, default) | 'prev' (desc) | dedup variants. */
	readonly direction?: IDBCursorDirection
}

/** Per-row visitor for `cursor`. Return `false` to stop iterating early. */
export type CursorVisitor<T> = (row: StoredRow<T>) => void | boolean | Promise<void | boolean>
